import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { externalKeysApi } from "../adapters/api";

const ExternalApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadKeys = useCallback(async () => {
    try {
      const data = await externalKeysApi.getList();
      setKeys(data);
    } catch (error) {
      console.error('Fehler beim Laden der API Keys:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der API Keys', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    try {
      const result = await externalKeysApi.create({ name: keyName.trim() });
      setCreatedKey(result);
      setKeyName('');
      loadKeys();
      setSnackbar({ open: true, message: 'API Key erstellt', severity: 'success' });
    } catch (error) {
      console.error('Fehler beim Erstellen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Erstellen des API Keys', severity: 'error' });
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setKeyName('');
    setCreatedKey(null);
  };

  const handleDelete = (key) => {
    setKeyToDelete(key);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await externalKeysApi.delete(keyToDelete.id);
      setSnackbar({ open: true, message: 'API Key geloescht', severity: 'success' });
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
      loadKeys();
    } catch (error) {
      console.error('Fehler beim Loeschen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Loeschen des API Keys', severity: 'error' });
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    setSnackbar({ open: true, message: 'Key in Zwischenablage kopiert', severity: 'success' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">API Keys</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Neuen Key erstellen
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell width={80}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    Keine API Keys vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id} hover>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={key.active !== false ? 'Aktiv' : 'Inaktiv'}
                      color={key.active !== false ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {key.createdAt ? new Date(key.createdAt).toLocaleString('de-DE') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(key)}
                      sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.light', color: 'white' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{createdKey ? 'API Key erstellt' : 'Neuen API Key erstellen'}</DialogTitle>
        <DialogContent>
          {createdKey ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Speichern Sie diesen Key jetzt. Er wird nicht erneut angezeigt.
              </Alert>
              <TextField
                fullWidth
                label="API Key"
                value={createdKey.apiKey}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCopyKey(createdKey.apiKey)}>
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ fontFamily: 'monospace' }}
              />
            </Box>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label="Name"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                  e.preventDefault();
                  if (keyName.trim()) handleCreate();
                }
              }}
              sx={{ mt: 1 }}
              placeholder="z.B. Produktion, Test, Partner XY"
            />
          )}
        </DialogContent>
        <DialogActions>
          {createdKey ? (
            <Button onClick={handleCloseCreateDialog} variant="contained">
              Schliessen
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseCreateDialog}>Abbrechen</Button>
              <Button onClick={handleCreate} variant="contained" disabled={!keyName.trim()}>
                Erstellen
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm">
        <DialogTitle>API Key loeschen</DialogTitle>
        <DialogContent>
          <Typography>
            Soll der API Key <strong>{keyToDelete?.name}</strong> wirklich geloescht werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Loeschen
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExternalApiKeys;
